import { IconButton } from '@mui/material'
import React, { useState, useRef } from 'react'
import { apiDelete, apiPut, apiGet } from '../../generic/Api_Functions'
import AddElement from '../../generic/smartAddElement/AddElement'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { Edit, TextSnippetOutlined } from '@mui/icons-material'
import ToolReferenceParameterValue from './ToolReferenceParameterValue'
import { useNavigate } from 'react-router-dom'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { systemValueCodeLabel } from '../../generic/systemValues/systemValueLabel'
import Document from '../document/Document'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import QrCodeGeneratorDialog, { buildAvailableFields } from '../../generic/qr/QrCodeGeneratorDialog'

const Tool = (props) => {
  // Historia 170 (J4b, S104) — `preselectedRecord` additive, optional, inert when absent (every OTHER
  // caller): `ToolReference.jsx` forwards this when a document repository row's "Open associated
  // record" preselects a TOOL inside this reference.
  const { row, endpoint, hideColumns, onRowClick, compatibleFixtureCodes = [], showQrAction = false, preselectedRecord = null } = props
  const [editingRow, setEditingRow] = useState(null)
  const { t_ } = useSmartTranslation()
  const [showToolParametersDialog, setShowToolParametersDialog] = useState(false)
  const [showDocument, setShowDocument] = useState(false)
  const [showQr, setShowQr] = useState(false)
  const [rowTool, setRowTool] = useState(null)
  const tool = useRef(null)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const navigate = useNavigate()
  const isUserAuthorized = useAuthorization()

  const columns = [
    {
      name: t_('Code'),
      field: 'code',
      readOnly: true,
      unique: true
    },
    {
      name: t_('Name'),
      field: 'name',
      mandatory: true
    },
    {
      name: t_('Description'),
      field: 'description'
    },
    {
      name: t_('Price'),
      field: 'price',
      render: (row) => {
        if (row.price == null) return null
        const price = Number(row.price)
        return Number.isInteger(price) ? price : price.toFixed(2)
      }
    },
    {
      name: t_('Location'),
      field: 'location',
      readOnly: true,
      hide: hideColumns
    },
    {
      name: 'D',
      field: '',
      edit: false,
      render: (row) => {
        if (row.parameterValueList === undefined) {
          return null
        } else {
          const valueFound = row.parameterValueList.find((parameterValue) => parameterValue.name === 'Diameter')
          return (valueFound !== undefined ? valueFound.value : null)
        }
      }
    },
    {
      name: 'RC',
      field: 'description',
      edit: false,
      render: (row) => {
        if (row.parameterValueList === undefined) {
          return null
        } else {
          const valueFound = row.parameterValueList.find((parameterValue) => parameterValue.name === 'Corner Radius')
          return (valueFound !== undefined ? valueFound.value : null)
        }
      }
    },
    {
      name: 'TL',
      field: 'description',
      edit: false,
      render: (row) => {
        if (row.parameterValueList === undefined) {
          return null
        } else {
          const valueFound = row.parameterValueList.find((parameterValue) => parameterValue.name === 'Total Length')
          return (valueFound !== undefined ? valueFound.value : null)
        }
      }
    },
    {
      name: 'CL',
      field: 'description',
      charLimit: 200,
      edit: false,
      render: (row) => {
        if (row.parameterValueList === undefined) {
          return null
        } else {
          const valueFound = row.parameterValueList.find((parameterValue) => parameterValue.name === 'Cutting Length')
          return (valueFound !== undefined ? valueFound.value : null)
        }
      }
    },
    {
      name: 'Z',
      field: 'description',
      edit: false,
      render: (row) => {
        if (row.parameterValueList === undefined) {
          return null
        } else {
          const valueFound = row.parameterValueList.find((parameterValue) => parameterValue.name === 'Number of Flutes')
          return (valueFound !== undefined ? valueFound.value : null)
        }
      }
    },
    {
      name: t_('Life') + '\n (' + t_('Current/Total') + ')',
      field: 'currentLife',
      readOnly: true
    },
    {
      name: t_('State'),
      field: 'status',
      readOnly: true,
      // P19 (story 204): the tool state Sun writes (ToolMapper: AVAILABLE, BROKEN, BLOCKED UP).
      render: (row) => systemValueCodeLabel(t_, 'ToolStatus', row.status)
    },
    {
      name: t_('Holder'),
      field: 'fixture.code',
      type: 'select',
      edit: {
        source: async () => {
          if (editingRow !== null) { return await apiGet('fixturesAvailableByToolReference/' + editingRow.id) }
        },
        id: 'id',
        field: 'code'
      }
    },
    {
      name: t_('Entry date'),
      field: 'entryDate',
      type: 'dateTimePicker',
      readOnly: false,
      numeric: true,
      mandatory: true
    }
  ]

  const actionsToolInstancesToolParameter = {
    edit: {
      fetchFunction: (editingRow) => {
        editingRow.tool = tool.current
        const temp = { tool: { id: editingRow.tool.id }, value: editingRow.value, unit: editingRow.unit, toolParameter: { id: editingRow.id } }
        return apiPut('toolParameterValues/' + editingRow.tool.id, temp)
      }
    }
  }

  const handleDataVisibleInNextWindow = (row, userPermission) => {
    delete row['smart-table-index']
    delete row.quantity
    row.active = true
    row.userPermision = userPermission

    if (row.currentLife !== null) {
      const index = row.currentLife.indexOf('/')
      if (index !== -1) {
        row.totalLife = row.currentLife.substring(index + 1).trim()
        row.currentLife = row.currentLife.substring(0, index).trim()
      }
    }

    return row
  }

  const actions = {
    delete: {
      disabled: !isUserAuthorized('DELETE_TOOL'),
      fetchFunction: (rows) => {
        let deleteEndpoint = 'tools'
        let params = rows[0].id
        if (rows.length !== 1) {
          deleteEndpoint = 'toolsMulti'
          params = rows.map(row => row.id)
        }
        return apiDelete(deleteEndpoint, params)
      }
    },

    custom: [
      {
        name: 'Documents',
        render: (row) => {
          return (
            <IconButton>
              <TextSnippetOutlined />
            </IconButton>
          )
        },
        disabled: editingRow !== null || !isUserAuthorized('DOCUMENTS_TOOL'),
        onClick: (row) => {
          setRowTool(row)
          setShowDocument(true)
        }
      },
      {
        name: 'Edit',
        render: (toolInstance) => {
          return (
            <IconButton>
              <Edit />
            </IconButton>
          )
        },
        disabled: !isUserAuthorized('EDIT_TOOL'),
        onClick: (toolInstance) => navigate('/resources/toolManager/' + row.id + '/toolInstance/' + toolInstance.id, { state: { row: handleDataVisibleInNextWindow(toolInstance, true) } })
      },
      ...(showQrAction
        ? [{
            name: 'Generate QR',
            render: (row) => (
              <IconButton>
                <QrCode2Icon />
              </IconButton>
            ),
            disabled: editingRow !== null || !isUserAuthorized('GENERATE_QR_CODE'),
            onClick: (row) => {
              setRowTool(row)
              setShowQr(true)
            }
          }]
        : [])
    ]
  }
  const defaultToolbar = {
    renderSticky: {
      fetchFunction: (extraFunctions) => {
        const handleAlert = extraFunctions?.showAlert
        return (
          <AddElement
            disable={!isUserAuthorized('ADD_TOOL')}
            parameter={row.id}
            endpoint={endpoint}
            referenceName={row.name}
            title={t_('Add tools')}
            onAlert={handleAlert}
          />
        )
      }
    }
  }

  const dataFetch = async () => {
    let tools = await apiGet(endpoint + '/' + row.id)
    if (compatibleFixtureCodes.length > 0) {
      tools = tools.map(tool => ({ ...tool, disabled: !compatibleFixtureCodes.includes(tool.fixtureReference?.code) }))
    }
    return tools
  }

  const onEditIndexChanged = (editIndex, row) => {
    if (editIndex >= 0) {
      tool.current = row
      setEditingRow(row)
    }
  }
  return (
    <div>
      <SmartDialog
        closeCallback={() => setShowDocument(false)}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        renderComponent={<Document reference={rowTool} resourceType='TOOL' isDownloadingFile={isDownloadingFile} setIsDownloadingFile={setIsDownloadingFile} />}
        setOpen={showDocument}
      />
      {showQr && (
        <QrCodeGeneratorDialog
          open={showQr}
          onClose={() => setShowQr(false)}
          entityType='Tool'
          entityLabel={t_('Tool')}
          instance={rowTool}
          availableFields={buildAvailableFields(columns, rowTool, { exclude: ['location'] })}
        />
      )}
      <SmartDialog
        accept={false}
        cancel={false}
        close
        closeCallback={() => setShowToolParametersDialog(false)}
        setOpen={showToolParametersDialog}
        renderComponent={<ToolReferenceParameterValue
          actions={actionsToolInstancesToolParameter}
                         />}
      />
      <SmartMateriaUITable
        tableTuneUniqueKey='tool'
        columns={columns}
        title={row.name}
        dataFetch={dataFetch}
        actions={actions}
        preselectedRecord={preselectedRecord}
        multipleSelection={!hideColumns}
        dense
        toolbar={defaultToolbar}
        onEditIndexChanged={onEditIndexChanged}
        onRowClick={(row) => {
          if (onRowClick && typeof onRowClick === 'function' && row.id) {
            onRowClick(row)
          }
        }}
        extraFilterParameter={{ name: 'disabled', value: true }}
        extraFilterParameterMessage={t_('The fixture assigned to this tool is not compatible.')}
      />
    </div>
  )
}

export default Tool
